import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { AlertCircle, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNetwork } from '@/lib/contexts/NetworkContext';
import { useToken } from '@/lib/hooks/useAPI';
import {
  getNetworkFromAddress,
  isValidSparkAddress,
  looksLikePublicKey,
  type NetworkType,
} from '@/lib/spark/address';
import { truncateKey } from '@/lib/formatters';

// Define temporary types to be replaced with real types later
interface TokenMetadata {
  token_public_key: string;
  name: string;
  ticker: string;
  price_usd?: number;
  // New fields from API response
  pubkey?: string;
  priceUsd?: number;
  decimals?: number;
  iconUrl?: string;
  holderCount?: number;
}

interface Account {
  id: string;
  balance?: string;
  lastActivity?: string;
}

type SearchInputProps = {
  placeholder?: string;
  className?: string;
  variant?: 'default' | 'header';
  onSearch?: (query: string) => void;
};

// UUID pattern for transaction IDs
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_NO_HYPHENS_PATTERN = /^[0-9a-f]{32}$/i;
const TXID_64_CHAR_PATTERN = /^[0-9a-f]{64}$/i;

export function SearchInput({
  placeholder = 'Search for anything...',
  className,
  variant = 'default',
  onSearch,
}: SearchInputProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<{
    tokens: TokenMetadata[];
    accounts: Account[];
    matchedToken: TokenMetadata | null;
    searchResults: TokenMetadata[]; // Add this for token search results
    sparkAddress: string | null;
    txid: string | null;
    wrongNetwork: boolean;
    detectedNetwork: string | null;
  }>({
    tokens: [],
    accounts: [],
    matchedToken: null,
    searchResults: [], // Initialize
    sparkAddress: null,
    txid: null,
    wrongNetwork: false,
    detectedNetwork: null,
  });
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [inputFocused, setInputFocused] = useState(false);

  const { network, setNetwork } = useNetwork();
  const currentNetworkType = network.toUpperCase() as NetworkType;

  const [activeSearchPublicKey, setActiveSearchPublicKey] = useState<string | null>(null);
  const [networkForTokenFetch, setNetworkForTokenFetch] = useState<
    'MAINNET' | 'REGTEST' | undefined
  >(undefined);
  // Add state to track if we're doing a partial search or full pubkey search
  const [isPartialSearch, setIsPartialSearch] = useState(false);

  // Derive networkForTokenFetch for the useToken hook
  useEffect(() => {
    if (currentNetworkType === 'MAINNET' || currentNetworkType === 'REGTEST') {
      setNetworkForTokenFetch(currentNetworkType);
    } else {
      setNetworkForTokenFetch(undefined);
    }
  }, [currentNetworkType]);

  // Fetch token data when activeSearchPublicKey and a compatible network are set
  const {
    data: fetchedTokenData,
    isLoading: isFetchingToken,
    error: fetchTokenError,
  } = useToken(
    activeSearchPublicKey!, // Enabled dictates actual call
    { network: networkForTokenFetch! },
    // @ts-expect-error useToken likely uses react-query options internally
    { enabled: !!activeSearchPublicKey && !!networkForTokenFetch }
  );

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    const arr: {
      type:
        | 'token'
        | 'account'
        | 'token_match'
        | 'sparkAddress'
        | 'txid'
        | 'network_switch'
        | 'search_result';
      data: TokenMetadata | Account | string;
    }[] = [];

    // Order: matchedToken, searchResults, sparkAddress, txid, network_switch

    if (results.matchedToken) {
      arr.push({ type: 'token_match', data: results.matchedToken });
    }

    // Add search results if available
    if (results.searchResults && results.searchResults.length > 0) {
      for (const token of results.searchResults) {
        arr.push({ type: 'search_result', data: token });
      }
    }

    if (results.sparkAddress) {
      arr.push({ type: 'sparkAddress', data: results.sparkAddress });
    }

    if (results.txid) {
      arr.push({ type: 'txid', data: results.txid });
    }

    // Add network switch option if wrong network
    if (results.wrongNetwork && results.detectedNetwork) {
      arr.push({ type: 'network_switch', data: results.detectedNetwork });
    }

    // Note: Token and account results are now empty arrays
    // but keeping the structure for future real data implementation
    for (const token of results.tokens) arr.push({ type: 'token', data: token });
    for (const account of results.accounts) arr.push({ type: 'account', data: account });

    return arr;
  }, [results]);

  // Focus input when "/" is pressed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if not in an input or textarea element already
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset focusedIndex when results change or dropdown closes
  useEffect(() => {
    setFocusedIndex(flatResults.length > 0 ? 0 : -1);
  }, [flatResults.length]);

  // Scroll focused result into view
  useEffect(() => {
    if (focusedIndex >= 0 && resultsRefs.current[focusedIndex]) {
      resultsRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
        // setActiveSearchPublicKey(null); // Optionally clear active pubkey search on outside click
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search logic
  useEffect(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setActiveSearchPublicKey(null);
      setIsPartialSearch(false);
      setResults({
        tokens: [],
        accounts: [],
        matchedToken: null,
        searchResults: [],
        sparkAddress: null,
        txid: null,
        wrongNetwork: false,
        detectedNetwork: null,
      });
      setShowResults(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true); // Set loading true initially for any search query

    const timerId = setTimeout(() => {
      const lowerQuery = trimmedQuery.toLowerCase();

      if (looksLikePublicKey(trimmedQuery)) {
        setActiveSearchPublicKey(trimmedQuery);
        setIsPartialSearch(false);
        // isLoading will be managed by the token fetching useEffect
        // No need to setResults here directly for matchedToken, let the other effect handle it.
        // We also don't setIsLoading(false) here; it's handled by the token fetch effect.
      } else {
        // This is a partial search term
        setActiveSearchPublicKey(trimmedQuery);
        setIsPartialSearch(true);

        let detectedSparkAddress: string | null = null;
        let detectedTxid: string | null = null;
        let wrongNetwork = false;
        let detectedNetworkString: string | null = null;

        if (
          UUID_PATTERN.test(trimmedQuery) ||
          UUID_NO_HYPHENS_PATTERN.test(trimmedQuery) ||
          TXID_64_CHAR_PATTERN.test(trimmedQuery)
        ) {
          detectedTxid = trimmedQuery;
        } else if (lowerQuery.startsWith('sp')) {
          const isValid = isValidSparkAddress(trimmedQuery);
          if (isValid) {
            detectedSparkAddress = trimmedQuery;
            const addressNetwork = getNetworkFromAddress(trimmedQuery);
            if (addressNetwork && addressNetwork !== currentNetworkType) {
              wrongNetwork = true;
              detectedNetworkString = addressNetwork;
            }
          }
        }

        // Let the token fetch effect handle search results via activeSearchPublicKey
        // Only update non-token related results here
        setResults((prev) => ({
          ...prev,
          sparkAddress: detectedSparkAddress,
          txid: detectedTxid,
          wrongNetwork,
          detectedNetwork: detectedNetworkString,
        }));

        // Don't set isLoading false here for token searches
        // This will be handled by the token fetch effect
        if (!trimmedQuery || detectedSparkAddress || detectedTxid) {
          setIsLoading(false);
          setShowResults(!!detectedSparkAddress || !!detectedTxid || wrongNetwork);
        }
      }
    }, 300); // Debounce

    return () => clearTimeout(timerId);
  }, [query, currentNetworkType]);

  // Effect to handle results from useToken hook
  useEffect(() => {
    if (!activeSearchPublicKey) {
      return;
    }

    if (isFetchingToken) {
      setIsLoading(true); // Ensure global loading is true while fetching token
    } else {
      setIsLoading(false); // Token fetch attempt is complete

      // For search results or single token match
      if (fetchedTokenData && networkForTokenFetch) {
        if (Array.isArray(fetchedTokenData)) {
          // We have search results
          const mappedResults = fetchedTokenData.map((token) => ({
            token_public_key: token.pubkey,
            name: token.name,
            ticker: token.ticker,
            price_usd: token.priceUsd,
            // Include other fields that might be useful
            pubkey: token.pubkey,
            decimals: token.decimals,
            iconUrl: token.iconUrl,
            holderCount: token.holderCount,
          }));

          setResults((prev) => ({
            ...prev,
            searchResults: mappedResults,
            matchedToken: null, // Clear any previous token match
          }));
          setShowResults(mappedResults.length > 0);
        } else {
          // We have a single token
          const tokenMetadata = fetchedTokenData.metadata;
          const newMatchedToken = {
            token_public_key: activeSearchPublicKey,
            name: tokenMetadata.name,
            ticker: tokenMetadata.ticker,
            price_usd: tokenMetadata.priceUsd,
            // Include other metadata fields as needed
            decimals: tokenMetadata.decimals,
            iconUrl: tokenMetadata.iconUrl,
            holderCount: tokenMetadata.holderCount,
          };

          setResults((prev) => ({
            ...prev,
            matchedToken: newMatchedToken,
            searchResults: [], // Clear search results when exact match found
          }));
          setShowResults(true);
        }
      } else if (fetchTokenError && networkForTokenFetch) {
        // Error case - could be 404 or other issue
        if (isPartialSearch) {
          // If this was a partial search that returned nothing, just show empty
          setResults((prev) => ({
            ...prev,
            searchResults: [],
            matchedToken: null,
          }));
          setShowResults(false);
        } else {
          // If this was a full pubkey that wasn't found, show error
          const newMatchedToken = {
            token_public_key: activeSearchPublicKey,
            name: 'Token not found',
            ticker: 'Error',
          };

          setResults((prev) => ({
            ...prev,
            matchedToken: newMatchedToken,
            searchResults: [],
          }));
          setShowResults(true);
        }
      } else if (activeSearchPublicKey && !networkForTokenFetch) {
        // Pubkey entered, but current network is not supported by useToken
        const newMatchedToken = {
          token_public_key: activeSearchPublicKey,
          name: 'Token (Unsupported Network)',
          ticker: 'N/A',
        };

        setResults((prev) => ({
          ...prev,
          matchedToken: newMatchedToken,
          searchResults: [],
        }));
        setShowResults(true);
      }
    }
  }, [
    fetchedTokenData,
    isFetchingToken,
    fetchTokenError,
    activeSearchPublicKey,
    networkForTokenFetch,
    isPartialSearch,
  ]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (showResults && focusedIndex >= 0 && flatResults[focusedIndex]) {
        const item = flatResults[focusedIndex];
        handleNavigateToResult(item);
      } else if (query.trim()) {
        handleSearchNavigation(query);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showResults || flatResults.length === 0) return;
      setFocusedIndex((prev) => (prev + 1) % flatResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showResults || flatResults.length === 0) return;
      setFocusedIndex((prev) => (prev - 1 + flatResults.length) % flatResults.length);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      inputRef.current?.blur();
      setShowResults(false);
    }
  };

  const handleNavigateToResult = (item: (typeof flatResults)[number]) => {
    if (item.type === 'token' || item.type === 'search_result') {
      const token = item.data as TokenMetadata;
      const tokenId = token.token_public_key || token.pubkey || '';
      router.push(`/token/${tokenId}`);
      if (onSearch) onSearch(tokenId);
    } else if (item.type === 'account') {
      const account = item.data as Account;
      router.push(`/address/${account.id}`);
      if (onSearch) onSearch(account.id);
    } else if (item.type === 'token_match') {
      const token = item.data as TokenMetadata;
      router.push(`/token/${token.token_public_key}`);
      if (onSearch) onSearch(token.token_public_key);
    } else if (item.type === 'sparkAddress') {
      // Navigate to the account page with the spark address
      router.push(`/address/${item.data}`);
      if (onSearch) onSearch(item.data as string);
    } else if (item.type === 'txid') {
      router.push(`/tx/${item.data}`);
      if (onSearch) onSearch(item.data as string);
    } else if (item.type === 'network_switch') {
      // Switch network and retry search with same query
      const newNetwork = (item.data as string).toLowerCase();
      setNetwork(newNetwork as string);
      // Don't clear query so user can search again in new network
      setShowResults(false);
      return;
    }

    setQuery('');
    setShowResults(false);
  };

  // Determine the appropriate navigation path based on query content
  const handleSearchNavigation = (searchQuery: string) => {
    if (onSearch) {
      onSearch(searchQuery);
    }

    // Close the dropdown
    setShowResults(false);

    // Handle different query types
    const trimmedQuery = searchQuery.trim();

    if (looksLikePublicKey(trimmedQuery)) {
      // This is a public key, navigate to token page
      router.push(`/token/${trimmedQuery}`);
    } else if (
      UUID_PATTERN.test(trimmedQuery) ||
      UUID_NO_HYPHENS_PATTERN.test(trimmedQuery) ||
      TXID_64_CHAR_PATTERN.test(trimmedQuery)
    ) {
      // This is a transaction ID
      router.push(`/tx/${trimmedQuery}`);
    } else if (searchQuery.startsWith('sp')) {
      // This appears to be a Spark address
      // Check if it's valid for the current network
      const isValid = isValidSparkAddress(trimmedQuery, currentNetworkType);

      if (isValid) {
        router.push(`/address/${trimmedQuery}`);
      } else {
        // Check if it's valid for another network
        const detectedNetwork = getNetworkFromAddress(trimmedQuery);

        if (detectedNetwork) {
          // Show wrong network message and option to switch
          setResults((prev) => ({
            ...prev,
            wrongNetwork: true,
            detectedNetwork,
          }));
        } else {
          // Not a valid address for any network, just try to navigate anyway
          router.push(`/address/${trimmedQuery}`);
        }
      }
    } else if (searchQuery.startsWith('token_')) {
      router.push(`/token/${searchQuery}`);
    } else {
      // For now, just log that no specific match was found
      // This will be replaced with real search functionality later
      console.log('No specific match found for:', searchQuery);
    }
  };

  // Determine icon and input size based on variant
  const isHeader = variant === 'header';
  const iconClasses = isHeader
    ? 'absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground'
    : 'absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground';

  const inputClasses = isHeader
    ? 'w-full rounded-lg bg-background dark:bg-card pl-8'
    : 'w-full rounded-lg bg-background dark:bg-card pl-12 pr-4 py-4 h-16 text-lg';

  // Common styles for result items
  const resultItemClasses = 'w-full px-4 py-3 text-left hover:bg-accent cursor-pointer';

  return (
    <div className={cn('relative w-full', className)} ref={searchRef}>
      <Search className={iconClasses} />
      <Input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        className={inputClasses}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleSearch}
        onFocus={() => setInputFocused(true)}
        onBlur={() => setInputFocused(false)}
      />

      {/* Keyboard shortcut indicator */}
      {!inputFocused && !query && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          <div className="hidden sm:flex items-center justify-center rounded border border-border px-1.5 h-6 text-xs text-muted-foreground font-mono">
            /
          </div>
        </div>
      )}

      {/* Results dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 w-full mt-2 rounded-md border bg-popover shadow-md z-50">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading results...</div>
          ) : (
            <div className="py-2">
              {flatResults.length === 0 ? (
                <div className="px-4 py-2 text-sm text-muted-foreground">No results found</div>
              ) : (
                <>
                  {results.matchedToken && (
                    <div>
                      <div className="px-4 py-1 text-sm font-medium text-muted-foreground">
                        Token
                      </div>
                      <button
                        type="button"
                        ref={(el) => {
                          resultsRefs.current[0] = el;
                        }}
                        className={`${resultItemClasses} ${
                          focusedIndex === 0 ? 'bg-accent/70' : ''
                        }`}
                        onClick={() => {
                          handleNavigateToResult({
                            type: 'token_match',
                            data: results.matchedToken as TokenMetadata,
                          });
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-mono text-base truncate">
                            {results.matchedToken.name}{' '}
                            <span className="text-muted-foreground">
                              ${results.matchedToken.ticker}
                            </span>
                          </span>
                          {results.matchedToken.name !== 'Token (from PubKey)' && (
                            <span className="text-sm text-muted-foreground">
                              {truncateKey(results.matchedToken.token_public_key, 12, 12)}
                            </span>
                          )}
                          <span className="text-sm text-muted-foreground">View token details</span>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Token search results */}
                  {results.searchResults && results.searchResults.length > 0 && (
                    <div>
                      <div className="px-4 py-1 text-sm font-medium text-muted-foreground">
                        Tokens
                      </div>
                      {results.searchResults.map((token, index) => {
                        const resultIndex = (results.matchedToken ? 1 : 0) + index;

                        return (
                          <button
                            key={token.token_public_key || token.pubkey || index}
                            type="button"
                            ref={(el) => {
                              resultsRefs.current[resultIndex] = el;
                            }}
                            className={`${resultItemClasses} ${
                              focusedIndex === resultIndex ? 'bg-accent/70' : ''
                            }`}
                            onClick={() => {
                              handleNavigateToResult({
                                type: 'search_result',
                                data: token,
                              });
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-mono text-base truncate">
                                {token.name}{' '}
                                <span className="text-muted-foreground">
                                  ${token.ticker.toUpperCase()}
                                </span>
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {truncateKey(
                                  (token.token_public_key || token.pubkey || '') as string,
                                  12,
                                  12
                                )}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {results.sparkAddress && (
                    <div>
                      <div className="px-4 py-1 text-sm font-medium text-muted-foreground">
                        Spark Address
                      </div>
                      <button
                        type="button"
                        ref={(el) => {
                          let refIndex = 0;
                          if (results.matchedToken) refIndex++;
                          if (results.searchResults) refIndex += results.searchResults.length;
                          resultsRefs.current[refIndex] = el;
                        }}
                        className={`${resultItemClasses} ${
                          focusedIndex ===
                          (results.matchedToken ? 1 : 0) +
                            (results.searchResults ? results.searchResults.length : 0)
                            ? 'bg-accent/70'
                            : ''
                        }`}
                        onClick={() => {
                          handleNavigateToResult({
                            type: 'sparkAddress',
                            data: results.sparkAddress as string,
                          });
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-mono text-base truncate">
                            {truncateKey(results.sparkAddress, 12, 12)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            View account details
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {results.txid && (
                    <div>
                      <div className="px-4 py-1 text-sm font-medium text-muted-foreground">
                        Transaction
                      </div>
                      <button
                        type="button"
                        ref={(el) => {
                          let refIndex = 0;
                          if (results.matchedToken) refIndex++;
                          if (results.searchResults) refIndex += results.searchResults.length;
                          if (results.sparkAddress) refIndex++;
                          resultsRefs.current[refIndex] = el;
                        }}
                        className={`${resultItemClasses} ${
                          focusedIndex ===
                          (results.matchedToken ? 1 : 0) +
                            (results.searchResults ? results.searchResults.length : 0) +
                            (results.sparkAddress ? 1 : 0)
                            ? 'bg-accent/70'
                            : ''
                        }`}
                        onClick={() => {
                          handleNavigateToResult({
                            type: 'txid',
                            data: results.txid as string,
                          });
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-mono text-base truncate">
                            {truncateKey(results.txid, 10, 8)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            View transaction details
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {results.wrongNetwork && results.detectedNetwork && (
                    <div>
                      <div className="px-4 py-1 text-sm font-medium text-muted-foreground">
                        Network Mismatch
                      </div>
                      <button
                        type="button"
                        ref={(el) => {
                          let refIndex = 0;
                          if (results.matchedToken) refIndex++;
                          if (results.searchResults) refIndex += results.searchResults.length;
                          if (results.sparkAddress) refIndex++;
                          if (results.txid) refIndex++;
                          resultsRefs.current[refIndex] = el;
                        }}
                        className={`${resultItemClasses} ${
                          focusedIndex ===
                          (results.matchedToken ? 1 : 0) +
                            (results.searchResults ? results.searchResults.length : 0) +
                            (results.sparkAddress ? 1 : 0) +
                            (results.txid ? 1 : 0)
                            ? 'bg-accent/70'
                            : ''
                        }`}
                        onClick={() => {
                          handleNavigateToResult({
                            type: 'network_switch',
                            data: results.detectedNetwork as string,
                          });
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-500" />
                          <div className="flex flex-col">
                            <span className="text-base">
                              Address is from {results.detectedNetwork} network
                            </span>
                            <span className="text-sm text-muted-foreground">
                              Click to switch networks
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
